/* eslint-disable no-nested-ternary */
/* eslint-disable sort-keys */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import {
  Container, Loader, Table
} from 'semantic-ui-react';

import '../css/StudentAssignmentView.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { ASSIGNMENT_MENU_STATUS } from '../managers/AssignmentManager';
import { VIEW_SELECTION } from '../managers/NavigationManager';

import StudentAssignmentService from '../services/StudentAssignmentService';

const StudentAssignmentView = observer((props) => {
  const { location, t } = props;

  const {
    appManager, assignmentManager, classroomManager, navigationManager,
    studentAssignmentManager, tagContentManager
  } = useContext(MobXProviderContext);

  // cache each component via useMemo to avoid re-mounting on every render
  const AssignmentMenu = useMemo(() => SatCoreComponent('AssignmentMenu'), []);
  const NoAssignmentsMessage = useMemo(() => SatCoreComponent('NoAssignmentsMessage'), []);
  const StudentAssignmentListCard = useMemo(() => SatCoreComponent('StudentAssignmentListCard'), []);
  const StudentAssignmentPlayerLauncher = useMemo(() => SatCoreComponent('StudentAssignmentPlayerLauncher'), []);

  const menuStatusOrder = studentAssignmentManager.studentAssignmentMenuStatusOrderArray;
  const storedMenuStatus = studentAssignmentManager.currentStudentAssignmentMenuStatus;

  const { initializingApp } = appManager;
  const firstTabOrCurrent = menuStatusOrder?.[0] || ASSIGNMENT_MENU_STATUS.CURRENT;
  const canRestoreStoredMenuStatus = menuStatusOrder?.includes(storedMenuStatus);

  // Edge case: during init, storedMenuStatus is force-set by InitService (to CURRENT) and does
  // not reflect user intent — use the satellite's configured first tab instead. Post-init,
  // restore stored if still valid for this satellite; otherwise fall back to first tab.
  const initialMenuStatus = initializingApp ? (firstTabOrCurrent) : (
    canRestoreStoredMenuStatus ? storedMenuStatus : firstTabOrCurrent
  );
  const [menuStatus, setMenuStatus] = useState(initialMenuStatus);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(t('DefaultSortOrder', 'asc'));
  const [loading, setLoading] = useState(false);

  /**
   * Refetch after init completes so assignmentArray matches the active tab.
   *
   * Note: in practice, this is an edge case that should only be triggered when
   * `studentAssignmentMenuStatusOrderArray` for the given satellite is configured
   * to NOT have 'current' as the first in the list AND if the user initially lands
   * on the StudentAssignmentView upon satellite initialization.
   */
  const pendingPostInitFetch = useRef(initializingApp && initialMenuStatus !== ASSIGNMENT_MENU_STATUS.CURRENT);
  const [initialRefetching, setInitialRefetching] = useState(pendingPostInitFetch.current);

  const [assignmentResourceFilter, setAssignmentResourceFilter] = useState('all-assignments');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('view')) {
      navigationManager.setView(urlParams.get('view'));
    } else {
      navigationManager.setView(VIEW_SELECTION.ASSIGNMENTS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Note: player-close refetch is handled centrally by StudentAssignmentPlayerLauncherService.closeActivePlayer.
  // TODO remove
  // // Refetch when a player modal closes so card state reflects any progress
  // // the student made during the launch (status, submitted, etc.).
  // const { activePlayer } = studentAssignmentPlayerLauncherManager;
  // const prevActivePlayer = useRef(activePlayer);
  // useEffect(() => {
  //   if (prevActivePlayer.current && !activePlayer) {
  //     fetchAssignments(menuStatus, search, sort);
  //   }
  //   prevActivePlayer.current = activePlayer;
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [activePlayer]);

  // Fires only in the edge case documented on `pendingPostInitFetch` above.
  useEffect(() => {
    if (initializingApp || !pendingPostInitFetch.current) {
      return;
    }
    pendingPostInitFetch.current = false;
    (async () => {
      await fetchAssignments(menuStatus, search, sort);
      setInitialRefetching(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializingApp]);

  const handleChangeAssignmentResourceFilter = (_event, data) => {
    setAssignmentResourceFilter(data.value);
    // pass the new filter explicitly — assignmentResourceFilter closure
    // is still the OLD value here because setAssignmentResourceFilter is async.
    fetchAssignments(menuStatus, search, sort, null, 0, false, true, data.value);
  };

  const fetchAssignments = async (
    nextMenuStatus, nextSearch, nextSort, contentTypes = null,
    currentPage = 0, _dueDate = false, clear = true, nextFilter = null
  ) => {
    if (appManager.initializingApp) {
      return;
    }

    if (nextFilter === null) {
      nextFilter = (menuStatus !== nextMenuStatus) ? 'all-assignments' : assignmentResourceFilter;
    }
    const resourceTypes = nextFilter === 'assessments-only'
      ? 'learnosity_activity_resource,assessment'
      : null;

    setAssignmentResourceFilter(nextFilter);
    setLoading(true);
    setMenuStatus(nextMenuStatus);
    setSearch(nextSearch);
    setSort(nextSort);

    const { status, dueToday, filterDateStr } = (
      StudentAssignmentService.getFetchParamsForStudentAssignmentMenuStatus(nextMenuStatus)
    );

    assignmentManager.setAssignmentStatus(status);

    await assignmentManager.fetchStudentAssignments({
      classroomId: classroomManager.currentClassroomId,
      filterDateStr,
      status,
      assignmentType: null,
      search: nextSearch,
      contentTypes,
      assignerType: null,
      currentPage,
      dueToday,
      clear,
      pageSize: undefined,
      // sortDir: nextSort,
      searchAll: true,
      resourceTypes
    });
    setLoading(false);
  };

  const fetchTags = async () => {
    setLoading(true);
    await tagContentManager.fetchContentCategoryTagsByName();
    setLoading(false);
  };

  // TODO remove per DEMO-3099: today-is element no longer in scope
  // const todayStr = dateFormat(new Date(), 'fullDateNoYear', appManager.locale);

  const isLoading = (!assignmentManager.loaded && classroomManager.currentClassroomId)
    || studentAssignmentManager.loadingStudentAssignmentMenu
    || loading;
  const hasAssignments = assignmentManager.assignmentArray.length > 0;
  // Force loader during the post-init refetch so the stale CURRENT list does not flash under the active tab header.
  const showLoader = initialRefetching || (isLoading && !hasAssignments);

  const renderStudentAssignmentView = () => {
    return (
      <>
        <Container className='student-assignment-view-container' fluid>
          <h1 className='sr-only page-heading'>{t('AssignmentLabel')}</h1>
          <div className='student-assignment-list-cards-container'>
            <AssignmentMenu
              assignmentResourceFilter={assignmentResourceFilter}
              fetchAssignments={fetchAssignments}
              fetchTags={fetchTags}
              handleChangeAssignmentResourceFilter={handleChangeAssignmentResourceFilter}
              location={location}
              menuStatus={menuStatus}
              search={search}
              sort={sort} />
            {showLoader ? <Loader active /> : renderStudentAssignmentBody()}
          </div>
        </Container>
        <StudentAssignmentPlayerLauncher />
      </>
    );
  };

  const renderStudentAssignmentBody = () => {
    if (!hasAssignments) {
      return (
        <div
          aria-labelledby={`assignment-menu-tab-${menuStatus}`}
          className='assignment-menu-panel assignment-menu-panel-empty'
          id='assignment-menu-panel'
          role='tabpanel'>
          {/* resource-type filter dropdown now rendered inside AssignmentMenu */}
          <NoAssignmentsMessage message={
            t(ASSIGNMENT_MENU_STATUS.getNoAssignmentsMessage(menuStatus),
              'Missing translation in StudentAssignmentView')
          } />
        </div>
      );
    }
    return (
      <div
        aria-labelledby={`assignment-menu-tab-${menuStatus}`}
        className='assignment-menu-panel'
        id='assignment-menu-panel'
        role='tabpanel'>
        <Container className='search-gui student-assignment-view-container-inner'>
          {renderStudentAssignmentCardsTable()}
        </Container>
      </div>
    );
  };

  const renderStudentAssignmentCardsTable = () => {
    return (
      <Table aria-label={t(`aria${capitalize(menuStatus)}Assignments`)}
        // basic='very'
        className='student-assignment-list-cards'>
        {renderStudentAssignmentCardsTableHeader()}
        <Table.Body>
          {assignmentManager.assignmentArray.map((assignment) => {
            return (
              <StudentAssignmentListCard
                key={`StudentAssignmentListCard_${assignment.id}`}
                assignment={assignment}
                menuStatus={menuStatus} />
            );
          })}
        </Table.Body>
      </Table>
    );
  };

  const renderStudentAssignmentCardsTableHeader = () => {
    const isClosedOrGraded = menuStatus === ASSIGNMENT_MENU_STATUS.CLOSED
      || menuStatus === ASSIGNMENT_MENU_STATUS.GRADED;

    const renderHeaderCell = (className, headerText) => {
      return (
        <Table.HeaderCell className={className} scope='col'>
          {headerText}
        </Table.HeaderCell>
      );
    };

    return (
      <>
        {/**
          * note: header is visually hidden per DEMO-3099,
          * but present in the DOM for WCAG 1.3.1 accessibility purposes.
          */}
        <Table.Header>
          <Table.Row className='sr-only'>
            {renderHeaderCell('student-assignment-list-card-image', t('headerImage'))}
            {renderHeaderCell('student-assignment-list-card-title-wrapper', t('headerTitle'))}
            {isClosedOrGraded && renderHeaderCell('student-assignment-list-card-spacer', '')}
            {!isClosedOrGraded && renderHeaderCell('student-assignment-list-card-status', t('headerStatus'))}
            {!isClosedOrGraded && renderHeaderCell('student-assignment-list-card-due-date', t('headerDueDate'))}
            {renderHeaderCell('student-assignment-list-card-details', t('headerDetails'))}
            {renderHeaderCell('student-assignment-list-card-grade', t('headerGrade'))}
            {isClosedOrGraded && renderHeaderCell('student-assignment-list-card-feedback', t('headerFeedback'))}
            {renderHeaderCell('student-assignment-list-card-actions', t('headerActions'))}
          </Table.Row>
        </Table.Header>
      </>
    );
  };

  const capitalize = (str) => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
  };

  // TODO remove per DEMO-3099: today-is element no longer in scope
  // const renderTodayElement = (innerClassName = '') => {
  //   return (
  //     <div aria-labelledby='today-is date-display'
  //       className='date-element sa-today-element-img'
  //       role='img'>
  //       <div className={innerClassName}>
  //         <div className='today-is' id='today-is'>{t('todayIs')}</div>
  //         <div className='date-display' id='date-display'>{todayStr}</div>
  //       </div>
  //     </div>
  //   );
  // };

  return renderStudentAssignmentView();
});
export default StudentAssignmentView;

SatCoreRegister('StudentAssignmentView', StudentAssignmentView);
